import { Download, Receipt, Send } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatCommissionAmount, maskReferredUserId } from "@/lib/affiliateDashboard";
import type { PayableRow } from "./types";

type Props = {
  payable: PayableRow[];
  payableLoading: boolean;
  totalsByCurrency: [string, number][];
  isSuperAdmin: boolean;
  connectByUser: Map<string, { payouts_enabled: boolean }>;
  running: string | null;
  onExport: () => void;
  onMarkPaid: (row: PayableRow) => void;
  onPayViaStripe: (row: PayableRow) => void;
};

export const PayableTable = ({ payable, payableLoading, totalsByCurrency, isSuperAdmin, connectByUser, running, onExport, onMarkPaid, onPayViaStripe }: Props) => (
  <Card className="shadow-soft">
    <CardHeader>
      <div className="flex items-center justify-between gap-2">
        <div>
          <CardTitle>Payable commissions</CardTitle>
          <p className="text-sm text-muted-foreground">
            Approved commissions not yet attached to a payout batch.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onExport} disabled={payable.length === 0}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      {totalsByCurrency.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {totalsByCurrency.map(([currency, cents]) => (
            <Badge key={currency} variant="secondary" className="text-sm">
              {formatCommissionAmount(cents, currency)} total payable
            </Badge>
          ))}
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Affiliate</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead className="text-right">Commissions</TableHead>
              <TableHead className="text-right">Payable</TableHead>
              <TableHead>Min payout</TableHead>
              {isSuperAdmin ? <TableHead className="text-right">Actions</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {payableLoading ? (
              <TableRow>
                <TableCell colSpan={isSuperAdmin ? 6 : 5}>
                  <Skeleton className="h-8 w-full" />
                </TableCell>
              </TableRow>
            ) : payable.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isSuperAdmin ? 6 : 5} className="text-center text-muted-foreground">
                  No approved commissions awaiting payout.
                </TableCell>
              </TableRow>
            ) : (
              payable.map((row) => (
                <TableRow key={`${row.affiliate_user_id}-${row.currency}`}>
                  <TableCell className="font-mono text-xs">
                    {maskReferredUserId(row.affiliate_user_id)}
                  </TableCell>
                  <TableCell className="uppercase">{row.currency}</TableCell>
                  <TableCell className="text-right">{row.commission_count}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCommissionAmount(row.payable_cents, row.currency)}
                  </TableCell>
                  <TableCell>
                    {row.meets_minimum ? (
                      <Badge variant="default">Above</Badge>
                    ) : (
                      <Badge variant="outline">Below</Badge>
                    )}
                  </TableCell>
                  {isSuperAdmin ? (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  size="sm"
                                  onClick={() => onPayViaStripe(row)}
                                  disabled={
                                    !connectByUser.get(row.affiliate_user_id)?.payouts_enabled ||
                                    running === `${row.affiliate_user_id}-${row.currency}` ||
                                    running === "all"
                                  }
                                >
                                  <Send className="mr-1 h-3 w-3" />
                                  {running === `${row.affiliate_user_id}-${row.currency}` ? "Paying…" : "Pay via Stripe"}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            {!connectByUser.get(row.affiliate_user_id)?.payouts_enabled ? (
                              <TooltipContent>
                                Affiliate has not completed Stripe Connect onboarding.
                              </TooltipContent>
                            ) : null}
                          </Tooltip>
                        </TooltipProvider>
                        <Button size="sm" variant="outline" onClick={() => onMarkPaid(row)}>
                          <Receipt className="mr-1 h-3 w-3" />
                          Mark paid
                        </Button>
                      </div>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </CardContent>
  </Card>
);
